import Phaser from "phaser";
import { DynamicScene } from "./DynamicScene";
import { Songs } from "../utils/Types";
import { Jukebox } from "../utils/Jukebox";
import { Global } from "../utils/Global";
import { WheelSectionColors } from "../wheel/WheelSection";

export class MainMenuScene extends DynamicScene {
  private title: Phaser.GameObjects.BitmapText = null!;
  private startText: Phaser.GameObjects.BitmapText = null!;
  private gameJamLogo: Phaser.GameObjects.Image = null!;
  private forText: Phaser.GameObjects.BitmapText = null!;
  private gameJamText: Phaser.GameObjects.BitmapText = null!;
  private versionText: Phaser.GameObjects.BitmapText = null!;

  private backgroundShader?: Phaser.GameObjects.Shader;

  private graphics: Phaser.GameObjects.Graphics = null!;

  private jukebox!: Jukebox;
  private wheelGraphics: Phaser.GameObjects.Graphics = null!;

  private titleX: number = 0;
  private titleY: number = 0;
  private WHEEL_RADIUS = Global.minFontSize * 5;

  private cameraAnimationTween?: Phaser.Tweens.Tween;

  constructor() {
    super({ key: "MainMenuScene" });
  }

  override init(): void {
    super.init();
  }

  public create(): void {
    if (Global.enableShaders)
      this.setBackgroundShader(
        "shd_menuBackground",
        this.scale.width,
        this.scale.height,
      );

    this.titleX = this.scale.width / 2;
    this.titleY = this.scale.height / 4;

    // Only draw background graphics if shaders are disabled
    if (!Global.enableShaders) {
      this.graphics = new Phaser.GameObjects.Graphics(this);
      this.children.add(this.graphics);
      this.graphics.fillStyle(0x000055, 1);
      this.graphics.fillRect(0, 0, this.scale.width, this.scale.height);
    }

    // Create game jam attribution in bottom left corner
    this.createGameJamAttribution();

    // Create version text in bottom right corner
    this.createVersionText();

    // Create spinning wheel behind title
    this.createSpinningWheel();

    this.title = this.add.bitmapText(
      this.titleX,
      this.titleY,
      "fnt_paintBasic",
      "Circolo",
      Global.minFontSize * 6,
    );
    this.title.setOrigin(0.5);

    // Add bowtie animation to title (slowly angle left and right)
    this.createTitleBowtieAnimation();

    this.startText = this.add.bitmapText(
      this.scale.width / 2,
      this.scale.height / 2.15,
      "fnt_paintBasic",
      "Start",
      Global.minFontSize * 3,
    );
    this.startText.setOrigin(0.5);
    this.startText
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", () => {
        this.jukebox.StopMusic(() => {
          this.scene.start("LoadLevelScene");
        });
      })
      .on("pointerover", () => {
        // Grow by 10% on hover
        this.tweens.add({
          targets: this.startText,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 200,
          ease: "Power2",
        });
      })
      .on("pointerout", () => {
        // Return to normal size when not hovering
        this.tweens.add({
          targets: this.startText,
          scaleX: 1.0,
          scaleY: 1.0,
          duration: 200,
          ease: "Power2",
        });
      });

    // Initialize camera animation if setting enabled
    if (Global.enableCameraBobbing) {
      this.initializeCameraAnimation();
    }

    this.jukebox = new Jukebox(this);
    this.jukebox.PlaySong(Songs.MAIN_MENU);

    this.isReady = true;
  }

  private createTitleBowtieAnimation(): void {
    // Create a bowtie animation that slowly angles the title left and right
    this.tweens.add({
      targets: this.title,
      rotation: Phaser.Math.DegToRad(-5), // Rotate 5 degrees to the left
      duration: 3000, // 3 seconds
      ease: "Sine.easeInOut",
      yoyo: true, // Return to original position
      repeat: -1, // Infinite loop
    });
  }

  private createGameJamAttribution(): void {
    const padding = Global.minFontSize;
    const fontSize = Global.minFontSize * 1.5;

    // Create "For" text
    this.forText = this.add.bitmapText(
      padding,
      this.scale.height - padding,
      "fnt_paintBasic",
      "For",
      fontSize,
    );
    this.forText.setOrigin(0, 1); // Bottom-left origin

    // Create game jam logo
    this.gameJamLogo = this.add.image(
      this.forText.x + this.forText.width + padding * 0.5,
      this.scale.height - padding,
      "gameJamLogo",
    );
    this.gameJamLogo.setOrigin(0, 1); // Bottom-left origin
    this.gameJamLogo.setScale(0.3); // Smaller scale for bottom corner

    // Create "game jam." text
    this.gameJamText = this.add.bitmapText(
      this.gameJamLogo.x + this.gameJamLogo.displayWidth + padding * 0.5,
      this.scale.height - padding,
      "fnt_paintBasic",
      "game jam.",
      fontSize,
    );
    this.gameJamText.setOrigin(0, 1); // Bottom-left origin
  }

  private createVersionText(): void {
    const padding = Global.minFontSize;
    const fontSize = Global.minFontSize * 1.5;

    // Create version text in bottom right corner
    this.versionText = this.add.bitmapText(
      this.scale.width - padding,
      this.scale.height - padding,
      "fnt_paintBasic",
      `ver. ${Global.gameVersion}`,
      fontSize,
    );
    this.versionText.setOrigin(1, 1); // Bottom-right origin
  }

  private createSpinningWheel(): void {
    // Create wheel graphics
    this.wheelGraphics = new Phaser.GameObjects.Graphics(this);
    this.children.add(this.wheelGraphics);

    // Position wheel graphics at title center
    const wheelX = this.scale.width / 2;
    const wheelY = this.scale.height / 4;

    // Set the graphics position to the wheel center
    this.wheelGraphics.setPosition(wheelX, wheelY);

    // Draw wheel at origin (0,0) of the graphics object
    this.drawWheel(0, 0, this.WHEEL_RADIUS);

    // Start spinning animation (now rotates around the graphics origin, which is the wheel center)
    this.tweens.add({
      targets: this.wheelGraphics,
      rotation: Math.PI * 2, // Full rotation
      duration: 8000, // 8 seconds per rotation (slower)
      ease: "Linear",
      repeat: -1, // Infinite loop
    });
  }

  private drawWheel(x: number, y: number, radius: number): void {
    this.titleX = this.scale.width / 2;
    this.titleY = this.scale.height / 4;

    this.wheelGraphics.clear();

    const sectionAngle = (Math.PI * 2) / 8;
    const colors = [
      WheelSectionColors.RED,
      WheelSectionColors.GREEN,
      WheelSectionColors.BLUE,
      WheelSectionColors.YELLOW,
    ];

    for (let i = 0; i < 8; i++) {
      const startAngle = i * sectionAngle;
      const endAngle = (i + 1) * sectionAngle;

      const color = colors[i % 4];

      this.wheelGraphics.fillStyle(color, 0.3); // Semi-transparent so title shows through
      this.wheelGraphics.beginPath();
      this.wheelGraphics.moveTo(x, y);
      this.wheelGraphics.arc(x, y, radius, startAngle, endAngle);
      this.wheelGraphics.closePath();
      this.wheelGraphics.fillPath();
    }

    // Add a subtle border
    this.wheelGraphics.lineStyle(2, 0x888888, 0.5);
    this.wheelGraphics.strokeCircle(x, y, radius);
  }

  override repositionElements(width: number, height: number): void {
    // Update background shader size if it exists
    if (Global.enableShaders && this.backgroundShader) {
      this.setBackgroundShader("shd_menuBackground", width, height);
    }

    this.title.setPosition(this.titleX, this.titleY);
    this.startText.setPosition(width / 2, height / 2.1);

    // Only redraw background graphics if shaders are disabled
    if (!Global.enableShaders && this.graphics) {
      this.graphics.clear();
      this.graphics.fillStyle(0x000055, 1);
      this.graphics.fillRect(0, 0, width, height);
    }

    if (this.wheelGraphics) {
      this.wheelGraphics.setPosition(this.titleX, this.titleY);
      this.drawWheel(0, 0, this.WHEEL_RADIUS);
    }

    // Reposition game jam attribution in bottom left
    if (this.forText && this.gameJamLogo && this.gameJamText) {
      const padding = Global.minFontSize;

      this.forText.setPosition(padding, height - padding);

      this.gameJamLogo.setPosition(
        this.forText.x + this.forText.width + padding * 0.5,
        height - padding,
      );

      this.gameJamText.setPosition(
        this.gameJamLogo.x + this.gameJamLogo.displayWidth + padding * 0.5,
        height - padding,
      );
    }

    // Reposition version text in bottom right
    if (this.versionText) {
      const padding = Global.minFontSize;
      this.versionText.setPosition(width - padding, height - padding);
    }
  }

  private initializeCameraAnimation(): void {
    // Stop any existing animation
    if (this.cameraAnimationTween) {
      this.cameraAnimationTween.destroy();
    }

    // Define the subtle movement parameters
    const maxOffset = 3; // Maximum pixels to move from center
    const duration = 10000; // 8 seconds for a full cycle
    const easing = "Sine.easeInOut"; // Smooth easing

    // Create a looping tween that moves the camera in a subtle figure-8 pattern
    this.cameraAnimationTween = this.tweens.add({
      targets: this.cameras.main,
      scrollX: {
        from: -maxOffset,
        to: maxOffset,
      },
      scrollY: {
        from: -maxOffset * 0.5,
        to: maxOffset * 0.5,
      },
      duration: duration,
      ease: easing,
      yoyo: true,
      repeat: -1, // Infinite loop
      onUpdate: () => {
        // Add a slight rotation to the movement for more organic feel
        const time = this.time.now * 0.0005; // Very slow rotation
        const offsetX = Math.sin(time) * maxOffset * 0.3;
        const offsetY = Math.cos(time * 0.7) * maxOffset * 0.2;

        this.cameras.main.scrollX += offsetX * 0.1;
        this.cameras.main.scrollY += offsetY * 0.1;

        // Move the background shader along with the camera to prevent artifacts
        if (this.backgroundShader) {
          this.backgroundShader.x =
            this.scale.width / 2 + this.cameras.main.scrollX;
          this.backgroundShader.y =
            this.scale.height / 2 + this.cameras.main.scrollY;
        }
      },
    });
  }

  private setBackgroundShader(shaderKey: string, w: number, h: number): void {
    if (!Global.enableShaders) return;

    // If an old one exists, destroy it:
    this.backgroundShader?.destroy();

    // Pull the loaded GLSL source out of the cache:
    const fragSource = this.cache.shader.get(shaderKey).glsl;

    // Create the Shader with your fragmentSource and any uniforms:
    this.backgroundShader = this.add.shader(
      {
        name: shaderKey,
        fragmentSource: fragSource,
        initialUniforms: {
          resolution: { type: "2f", value: [w, h] },
        },
        setupUniforms: (
          setUniform: (name: string, value: unknown) => void,
          _drawingContext: unknown,
        ) => {
          setUniform("time", this.game.loop.getDuration());
        },
      },
      w / 2, // x-pos
      h / 2, // y-pos
      w, // width
      h, // height
      [], // textures
    );

    // Tidy up ordering:
    this.backgroundShader.setDepth(-1000); // Ensure it's behind everything
  }
}
